import { useMemo } from "react";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { onlineManager } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useAuth } from "../auth/AuthProvider";
import { SignIn } from "../auth/SignIn";
import { ScopeGate, type ResolvedScope } from "../auth/ScopeGate";
import { makeQueryClient, registerMutationDefaults, createPersistOptions } from "../offline";

export const Route = createFileRoute("/_authed")({ component: AuthedShell });

// Exported for tests (rendered without a router).
export function AuthedShell() {
  const auth = useAuth();

  if (auth.status === "loading") {
    return <div className="min-h-screen grid place-items-center text-slate-400" style={{ background: "#eef2f4" }}>Loading…</div>;
  }
  if (auth.status === "signedOut" || !auth.user) return <SignIn />;

  return (
    <ScopeGate uid={auth.user.uid}>
      {(scope) => <BoardSession uid={auth.user!.uid} scope={scope} />}
    </ScopeGate>
  );
}

// The query client — and the IndexedDB store behind it — belong to ONE (uid, business, site)
// session. Remounting per session is what makes the handover on a shared tablet clean: a
// different signed-in user gets a different client and a different persisted store, so neither
// the board nor the offline outbox can carry over.
function BoardSession({ uid, scope }: { uid: string; scope: ResolvedScope }) {
  const sessionKey = `${uid}:${scope.businessId}:${scope.siteId}`;

  const queryClient = useMemo(() => {
    const qc = makeQueryClient();
    registerMutationDefaults(qc, scope);
    return qc;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by the session identity, not the object identity
  }, [sessionKey]);

  return (
    <PersistQueryClientProvider
      key={sessionKey}
      client={queryClient}
      persistOptions={createPersistOptions(uid, scope)}
      onSuccess={() => {
        // Cache rehydrated — drain anything this session queued while offline.
        if (onlineManager.isOnline()) void queryClient.resumePausedMutations();
      }}
    >
      <Outlet />
    </PersistQueryClientProvider>
  );
}
